import ProposalComputationRates from './rates/proposal-computation-rates'
import ProposalComputationEoITAP from './premium-and-eoi/proposal-computation-eoi-tap'
import { Spinner } from '../../../../../assets/icon/svg'
import { useProposalData } from '../../../../../hooks/populate-data/use-proposal-data'
import { setClearRatesTabs, setIsUpdateRates } from '../../../../../features/actuarial/rates-slice'
import { useDispatch } from 'react-redux'
import Documents from './documents/documents'
import Remarks from './remarks/remarks'
import ProposalComputationTabs from './proposal-computation-tabs'
import LoadingGlobal from '../../../../ui/loading-global'
import LoadingFile from '../../../../ui/loading-file'
import { useComputationActions } from '../../../../../hooks/actions/user-computation-actions'
import { useEffect, useMemo, useRef } from 'react'
import { resetRemarks } from '../../../../../features/actuarial/remark-slice'
import { clearFilesDocuments } from '../../../../../features/actuarial/document-slice'
import { useProposalComputation } from '../../../../../hooks/proposal/use-proposal-computation'
import CloseButtonModal from '../../../../ui/close-button-modal'
import { clearUpdateTypeModal } from '../../../../../features/gms-state-and-modal/modal-slice'

export default function ProposalComputation() {

    const dispatch = useDispatch();
    const { 
        proposal,
        isLoadingProposal,
        isErrorProposal,
        proposalErrorMessage,
        repopulateAll,
        repopulateRates,
        populateProposalNotesForPDF,
        populateProposalActuarialFiles,
        populateEvidenceNotes,
        populateTotalAmount
    } = useProposalData();
    const {
        isLoadingDownloadRatesTemplate,
        isLoadingUploadRatesExcel,
    } = useComputationActions();
    const {
        isEvidenceOfInsurability,
        isUpdateAnnualPremium,
        ratesActiveTab,
        isEditingNotesForProposal,
        isEditNotesForCFE,
        isUpdateDocuments,
        isUpdateRates,
    } = useProposalComputation();
    const isLoadingGlobal = isLoadingUploadRatesExcel;
    const isLoadingDownload = isLoadingDownloadRatesTemplate;

    const shouldStopPopulate =
        isEditingNotesForProposal ||
        isEditNotesForCFE ||
        isUpdateDocuments ||
        isEvidenceOfInsurability ||
        isUpdateAnnualPremium ||
        isUpdateRates;

    const populatedProposalIdRef = useRef<number | null>(null);

    const proposalSignature = useMemo(
        () => (proposal ? JSON.stringify(proposal) : null),
        [proposal],
    );

    useEffect(() => {
        if (!proposal?.application_id) {
            return;
        }

        // if (shouldStopPopulate) {
        //     return;
        // }

        if (populatedProposalIdRef.current === proposalSignature) {
            return;
        }

        populatedProposalIdRef.current = proposal.application_id;
        repopulateAll();
        repopulateRates();
    }, [
        proposal?.application_id,
        repopulateAll,
        repopulateRates,
        proposalSignature,
    ]);
    
    const handleClose = async () => {
        dispatch(clearUpdateTypeModal())
        dispatch(setClearRatesTabs())
        dispatch(clearFilesDocuments())
        dispatch(resetRemarks())
        dispatch(setIsUpdateRates(false))
        // await refetchProposal();
        repopulateAll();
        populateEvidenceNotes();
        populateTotalAmount();
        populateProposalActuarialFiles();
        populateProposalNotesForPDF();
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 sm:p-4">
            <div className="flex h-[96dvh] w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:max-w-[90vw] 2xl:h-[82dvh] lg:max-w-[700px] xl:max-w-[800px] 2xl:max-w-[1000px]">
                <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 md:px-5 md:py-4 lg:px-6">
                    <div className="min-w-0">
                        <h2 className="truncate text-sm font-semibold text-gray-800">
                            Proposal Computation
                        </h2>
                        <p className="text-xs text-gray-500">
                            Review rates, premiums, documents, and computation details.
                        </p>
                    </div>
                    <div className="ml-3 flex shrink-0 items-center justify-end">
                        <CloseButtonModal handleClose={handleClose} />
                    </div>
                </div>
                {isLoadingProposal ? (
                    <div className="flex min-h-0 flex-1 items-center justify-center">
                        <div className="flex flex-col items-center justify-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#186cb2]/10">
                                <Spinner color="#186cb2" size={24} />
                            </div>
                            <div className="text-center">
                                <p className="text-xs font-medium text-gray-700">
                                    Loading proposal computation
                                </p>
                                <p className="mt-1 text-[11px] text-gray-500">
                                    Please wait while the computation data is being prepared.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : isErrorProposal ? (
                    <div className="flex min-h-0 flex-1 items-center justify-center px-4">
                        <div className="max-w-sm text-center">
                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 2.82 16.5A2 2 0 0 0 4.54 19.5h14.92a2 2 0 0 0 1.72-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                                </svg>
                            </div>
                            <p className="text-xs font-semibold text-gray-800">
                                Unable to load proposal computation
                            </p>
                            <p className="mt-1 text-xs text-red-500">
                                {proposalErrorMessage}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex h-full min-h-0 flex-col bg-white">
                        <ProposalComputationTabs />
                        <div className="min-h-0 flex-1 px-4 py-2">
                            {ratesActiveTab === "rates" && (
                                <ProposalComputationRates />
                            )}
                            {ratesActiveTab === "eoi" && (
                                <ProposalComputationEoITAP />
                            )}
                            {ratesActiveTab === "actDocuments" && (
                                <Documents />
                            )}
                            {ratesActiveTab === "actNotes" && (
                                <Remarks />
                            )}
                        </div>
                    </div>
                )}
            </div>
            {isLoadingGlobal && <LoadingGlobal />}
            {isLoadingDownload && <LoadingFile />}
        </div>
    )
}